import { Edit, RefreshButton, SaveButton, useForm } from '@refinedev/antd';
import { useGo, useResourceParams } from '@refinedev/core';
import { Button, message, Tooltip } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ProtectedDeleteButton from '@/components/protected/ProtectedDeleteButton';
import { SERVICE_ORDER_STATUS, ServiceOrder } from '@/model/ServiceOrder/ServiceOrder';
import { ServiceOrderItem } from '@/model/ServiceOrder/ServiceOrderItem';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';
import { RESOURCES } from '@/Resources';

import ServiceOrderForm from './ServiceOrderForm/ServiceOrderForm';

const ServiceOrderEdit = () => {
  const go = useGo();
  const { id: resourceId } = useResourceParams();
  const { t } = useTranslation();
  const [serviceOrderItems, setServiceOrderItems] = useState<ServiceOrderItem[]>([]);
  const [hasInvalidDistribution, setHasInvalidDistribution] = useState(false);
  // Evita stale closures dentro de callbacks de refine (onMutationSuccess).
  const serviceOrderItemsRef = useRef<ServiceOrderItem[]>([]);
  const statusOverrideRef = useRef<SERVICE_ORDER_STATUS | null>(null);
  const { serviceOrderRepository } = useRepositoriesContext();
  const refetchItems = useCallback(() => {
    serviceOrderRepository
      .getServiceOrderItems(resourceId as number)
      .then(items => {
        setServiceOrderItems(items);
      })
      .catch(error => {
        message.error(t('Ocurrió un error al cargar los elementos de la orden de servicio'));
        console.error('Error fetching service order items:', error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourceId, serviceOrderRepository]);

  serviceOrderItemsRef.current = serviceOrderItems;
  const { formProps, saveButtonProps, query } = useForm<ServiceOrder>({
    redirect: false,
    successNotification: {
      message: t('Orden de servicio actualizada correctamente'),
      type: 'success',
    },
    onMutationSuccess(serviceOrderResponse) {
      if (formProps?.initialValues?.status == SERVICE_ORDER_STATUS.PENDIENTE_DE_COORDINACION) {
        statusOverrideRef.current = null;
        void query?.refetch?.();

        return;
      }

      void serviceOrderRepository
        .saveServiceOrderItems(serviceOrderItemsRef.current, serviceOrderResponse.data.id)
        .then(() => {
          if (statusOverrideRef.current === SERVICE_ORDER_STATUS.PENDIENTE_DE_FACTURACION) {
            void serviceOrderRepository
              .updateServiceOrder({
                id: serviceOrderResponse.data.id,
                status: statusOverrideRef.current,
              })
              .then(() => {
                statusOverrideRef.current = null;
                go({
                  to: {
                    resource: RESOURCES.SERVICE_ORDERS,
                    action: 'show',
                    id: serviceOrderResponse.data.id,
                  },
                  type: 'replace',
                });
              })
              .catch(error => {
                statusOverrideRef.current = null;
                message.error(t('Ocurrió un error al enviar la orden a facturación'));
                console.error('Error updating service order status to billing:', error);
                void query?.refetch().then(() => refetchItems());
              });
          } else {
            statusOverrideRef.current = null;
            go({
              to: {
                resource: RESOURCES.SERVICE_ORDERS,
                action: 'show',
                id: serviceOrderResponse.data.id,
              },
              type: 'replace',
            });
          }
        })
        .catch(error => {
          statusOverrideRef.current = null;
          message.error(t('Ocurrió un error al guardar los elementos utilizados'));
          console.error('Error saving service order items:', error);
          void query?.refetch().then(() => refetchItems());
        });
    },
    onMutationError() {
      statusOverrideRef.current = null;
      message.error(t('Ocurrió un error al actualizar la orden de servicio'));
      void query?.refetch().then(() => {
        refetchItems();
      });
    },
  });
  const saveButtonPropsWithValidation = {
    ...saveButtonProps,
    disabled: saveButtonProps?.disabled,
  };

  useEffect(() => {
    if (
      resourceId &&
      (formProps?.initialValues?.status == SERVICE_ORDER_STATUS.PENDIENTE_DE_FACTURACION ||
        formProps?.initialValues?.status == SERVICE_ORDER_STATUS.FINALIZADA)
    ) {
      go({
        to: {
          resource: RESOURCES.SERVICE_ORDERS,
          action: 'show',
          id: resourceId,
        },
        type: 'replace',
      });
    }
  }, [formProps, go, resourceId]);

  return (
    <Edit
      isLoading={query?.isLoading || query?.isFetching}
      headerButtons={({ refreshButtonProps }) => <RefreshButton {...refreshButtonProps} />}
      footerButtons={({ deleteButtonProps }) => (
        <>
          <Button
            danger
            onClick={() => {
              void serviceOrderRepository.updateServiceOrder({
                id: formProps.initialValues?.id as number,
                status: SERVICE_ORDER_STATUS.PENDIENTE_DE_COORDINACION,
              });
            }}>
            {t('Enviar a facturación sin guardar')}
          </Button>
          {!formProps.initialValues?.created_automatically && (
            <ProtectedDeleteButton {...deleteButtonProps} />
          )}
          <SaveButton {...saveButtonPropsWithValidation} />
          {formProps?.initialValues?.status === SERVICE_ORDER_STATUS.PENDIENTE_DE_COORDINACION && (
            <SaveButton
              {...saveButtonPropsWithValidation}
              onClick={() => {
                statusOverrideRef.current = SERVICE_ORDER_STATUS.COORDINADA;
                saveButtonProps.onClick?.();
              }}>
              {t('Guardar como coordinada')}
            </SaveButton>
          )}
          {(formProps?.initialValues?.status === SERVICE_ORDER_STATUS.COORDINADA ||
            formProps?.initialValues?.status === SERVICE_ORDER_STATUS.REALIZADA) && (
            <Tooltip
              title={
                hasInvalidDistribution ? t('Complete las cantidades de los elementos enviados') : ''
              }>
              <SaveButton
                {...saveButtonPropsWithValidation}
                disabled={hasInvalidDistribution || saveButtonPropsWithValidation.disabled}
                onClick={() => {
                  // Flujo esperado: guardar cambios y luego mover a facturación.
                  statusOverrideRef.current = SERVICE_ORDER_STATUS.PENDIENTE_DE_FACTURACION;
                  saveButtonProps.onClick?.();
                }}>
                {t('Guardar y enviar a facturación')}
              </SaveButton>
            </Tooltip>
          )}
        </>
      )}>
      {query?.data && (
        <ServiceOrderForm
          formProps={formProps}
          serviceOrderItems={serviceOrderItems}
          setServiceOrderItems={setServiceOrderItems}
          onInvalidDistributionChange={setHasInvalidDistribution}
          statusOverrideRef={statusOverrideRef}
        />
      )}
    </Edit>
  );
};

export default ServiceOrderEdit;
